import React, { useState } from 'react';
import { Upload, AlertCircle, CheckCircle, FileSearch, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import api from '../lib/api';

interface ConciliacaoResumo {
  total_lote: number;
  mantidos: number;
  removidos: number;
  nao_encontrados_excel: number;
}

interface BoletoRemovido {
  id: number;
  nosso_numero: string;
  nome_associado: string;
  data_vencimento: string;
  placa: string;
  valor_boleto: number;
}

interface ConciliacaoPreviewResponse {
  resumo: ConciliacaoResumo;
  boletos_removidos: BoletoRemovido[];
}

interface ConciliarBoletosModalProps {
  isOpen: boolean;
  onClose: () => void;
  loteId: number;
  onSuccess: () => void;
}

const ConciliarBoletosModal: React.FC<ConciliarBoletosModalProps> = ({
  isOpen,
  onClose,
  loteId,
  onSuccess
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ConciliacaoPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];

    if (!validTypes.includes(selectedFile.type)) {
      setError('Tipo de arquivo invalido. Use .xlsx, .xls ou .csv');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setPreview(null);
    setSuccess(false);

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post<ConciliacaoPreviewResponse>(
        `/disparos/lotes/${loteId}/conciliacao/preview`,
        formData
      );

      setPreview(response);
    } catch (err: any) {
      console.error('Erro ao fazer preview:', err);
      setError(err.message || 'Erro ao processar arquivo');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = async () => {
    if (!preview || preview.boletos_removidos.length === 0) return;

    setConfirming(true);
    setError(null);

    try {
      const boletoIds = preview.boletos_removidos.map(b => b.id);

      await api.post(`/disparos/lotes/${loteId}/conciliacao/confirmar`, {
        boleto_ids: boletoIds
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1500);
    } catch (err: any) {
      console.error('Erro ao confirmar conciliacao:', err);
      setError(err.message || 'Erro ao confirmar conciliacao');
    } finally {
      setConfirming(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    setSuccess(false);
    setConfirming(false);
    onClose();
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg">
              <FileSearch className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold">Conciliar Boletos</DialogTitle>
              <DialogDescription className="text-sm text-gray-600">
                Upload de planilha para conciliar boletos do lote
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {!preview && !loading && (
            <Alert className="bg-blue-50 border-blue-200">
              <FileSearch className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <div className="space-y-2">
                  <p className="font-medium">Como funciona a conciliacao:</p>
                  <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                    <li>Faca upload da planilha com os boletos validos</li>
                    <li>O sistema cruza pelo <strong>Nosso Numero</strong></li>
                    <li>Boletos que <strong>nao estiverem</strong> na planilha serao marcados como excluidos</li>
                  </ul>
                  <p className="text-sm mt-2">
                    Formato esperado: colunas <strong>Nome</strong>, <strong>Nosso Numero</strong>, <strong>Data Vencimento</strong>, <strong>Placas</strong>, <strong>Complemento</strong>
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Selecione o arquivo Excel
            </label>
            <div className="flex items-center gap-3">
              <label
                htmlFor="conciliacao-file-upload"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-orange-500 hover:bg-orange-50 transition-colors"
              >
                <Upload className="h-6 w-6 text-gray-400" />
                <div className="text-center">
                  <p className="text-sm text-gray-700">
                    {file ? file.name : 'Clique para selecionar ou arraste o arquivo'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Formatos: .xlsx, .xls, .csv
                  </p>
                </div>
              </label>
              <input
                id="conciliacao-file-upload"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                disabled={loading || confirming}
                className="hidden"
              />
            </div>
          </div>

          {loading && (
            <div className="text-center py-4">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto" />
              <p className="text-sm text-gray-600 mt-2">Processando arquivo...</p>
            </div>
          )}

          {error && (
            <Alert className="bg-red-50 border-red-200">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Conciliacao realizada com sucesso!
              </AlertDescription>
            </Alert>
          )}

          {preview && !loading && !success && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-blue-600">Total no Lote</p>
                    <p className="text-2xl font-bold text-blue-700">{preview.resumo.total_lote}</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-green-600">Permanecem</p>
                    <p className="text-2xl font-bold text-green-700">{preview.resumo.mantidos}</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-red-600">Serao Excluidos</p>
                    <p className="text-2xl font-bold text-red-700">{preview.resumo.removidos}</p>
                  </CardContent>
                </Card>
              </div>

              {preview.resumo.nao_encontrados_excel > 0 && (
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-800">
                    {preview.resumo.nao_encontrados_excel} registro(s) do Excel nao foram encontrados no lote.
                  </AlertDescription>
                </Alert>
              )}

              {preview.resumo.removidos === 0 && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Todos os boletos do lote estao presentes no arquivo. Nenhuma exclusao necessaria.
                  </AlertDescription>
                </Alert>
              )}

              {preview.resumo.mantidos === 0 && preview.resumo.total_lote > 0 && (
                <Alert className="bg-red-50 border-red-200">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 font-medium">
                    Nenhum boleto do lote foi encontrado no arquivo. Verifique se o arquivo esta correto.
                  </AlertDescription>
                </Alert>
              )}

              {preview.boletos_removidos.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-red-600 mb-2">
                    Boletos que serao excluidos ({preview.boletos_removidos.length})
                  </h3>
                  <div className="bg-red-50 rounded-lg overflow-hidden border border-red-200">
                    <div className="max-h-60 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-red-100 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-red-700 font-medium">Nome</th>
                            <th className="px-4 py-2 text-left text-red-700 font-medium">Nosso Numero</th>
                            <th className="px-4 py-2 text-left text-red-700 font-medium">Vencimento</th>
                            <th className="px-4 py-2 text-left text-red-700 font-medium">Placa</th>
                            <th className="px-4 py-2 text-right text-red-700 font-medium">Valor</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-red-200">
                          {preview.boletos_removidos.map((boleto) => (
                            <tr key={boleto.id} className="hover:bg-red-100/50">
                              <td className="px-4 py-2 text-gray-900">{boleto.nome_associado}</td>
                              <td className="px-4 py-2 text-gray-900">{boleto.nosso_numero}</td>
                              <td className="px-4 py-2 text-gray-900">{boleto.data_vencimento}</td>
                              <td className="px-4 py-2 text-gray-900">{boleto.placa}</td>
                              <td className="px-4 py-2 text-gray-900 text-right">{formatCurrency(boleto.valor_boleto)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="pt-6 gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={confirming}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          {preview && preview.resumo.removidos > 0 && !success && (
            <Button
              onClick={handleConfirmar}
              disabled={confirming}
              className="w-full sm:w-auto bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
            >
              {confirming ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Confirmando...
                </>
              ) : (
                `Confirmar Exclusao de ${preview.resumo.removidos} boleto(s)`
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConciliarBoletosModal;
